import React from "react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal } from "../components/Reveal";
import { CtaBand, Overline } from "../components/Primitives";
import { IMG } from "../lib/assets";
import { Seo } from "../components/Seo";

export default function Founder() {
  const { t } = useLang();
  const education = t("founder.education");
  const experience = t("founder.experience");
  const languages = t("founder.languages");

  return (
    <div data-testid="founder-page">
      <Seo pageKey="founder" path="/founder" />
      {/* Hero with portrait */}
      <section className="relative pt-32 md:pt-44 pb-16">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-end">
          <div className="md:col-span-7">
            <Reveal><Overline>{t("founder.heroOverline")}</Overline></Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-8 font-serif text-5xl sm:text-6xl md:text-7xl font-light leading-none tracking-tight text-kenosis-ivory">{t("founder.name")}</h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-4 font-sans text-xs uppercase tracking-[0.25em] text-kenosis-gold">{t("founder.role")}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-8 max-w-xl font-sans text-base md:text-lg font-light leading-relaxed text-kenosis-ivory/70">{t("founder.intro")}</p>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="md:col-span-5">
            <div className="relative aspect-[3/4] overflow-hidden">
              <img src={IMG.founder} alt={t("founder.name")} className="h-full w-full object-cover object-[center_25%]" onError={(e) => { e.currentTarget.src = IMG.inner; }} />
              <div className="absolute inset-0 bg-gradient-to-t from-kenosis-black/50 to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Journey */}
      <section className="relative py-16 md:py-24 border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12">
          <div className="md:col-span-4">
            <Reveal><h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight text-kenosis-ivory">{t("founder.journeyTitle")}</h2></Reveal>
          </div>
          <Reveal delay={0.1} className="md:col-span-8">
            <p className="font-sans text-base md:text-lg font-light leading-relaxed text-kenosis-muted">{t("founder.journey")}</p>
          </Reveal>
        </div>
      </section>

      {/* Education + Experience */}
      <section className="relative py-16 md:py-24 border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-16">
          <div>
            <Reveal><Overline>{t("founder.educationTitle")}</Overline></Reveal>
            <div className="mt-10">
              {education.map((e, i) => (
                <Reveal key={i} delay={i * 0.04} className="border-t border-white/10 py-6">
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="font-serif text-xl font-normal text-kenosis-ivory">{e.title}</h3>
                    {e.year && <span className="shrink-0 font-sans text-xs tracking-widest text-kenosis-gold/70">{e.year}</span>}
                  </div>
                  <p className="mt-2 font-sans text-sm font-light text-kenosis-muted">{e.place}</p>
                </Reveal>
              ))}
            </div>
          </div>
          <div>
            <Reveal><Overline>{t("founder.experienceTitle")}</Overline></Reveal>
            <div className="mt-10">
              {experience.map((e, i) => (
                <Reveal key={i} delay={i * 0.04} className="border-t border-white/10 py-6">
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="font-serif text-xl font-normal text-kenosis-ivory">{e.role}</h3>
                    <span className="shrink-0 font-sans text-xs tracking-widest text-kenosis-gold/70">{e.year}</span>
                  </div>
                  <p className="mt-2 font-sans text-sm font-light text-kenosis-muted">{e.place}</p>
                </Reveal>
              ))}
            </div>

            <Reveal className="mt-12">
              <Overline>{t("founder.languagesTitle")}</Overline>
              <div className="mt-8 flex flex-wrap gap-3">
                {languages.map((l, i) => (
                  <span key={i} className="border border-white/15 px-4 py-2 font-sans text-xs uppercase tracking-[0.15em] text-kenosis-ivory/80">{l}</span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="border border-kenosis-gold/20 bg-kenosis-surface p-8 md:p-12">
            <p className="font-sans text-sm font-light leading-relaxed text-kenosis-ivory/70">{t("founder.note")}</p>
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
